import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FileDown } from "lucide-react";
import { useAppDispatch } from "../../app/hooks";
import { downloadPortalRecord } from "../../features/portal/slice-extensions/portal-record-slice-extension";
import { formatDateOnly } from "../../features/training/utils/formatDateOnly";
import { getDisplayLocale } from "../../features/utils/timezoneUtils";

interface PortalRecordRowProps {
  memberId: string;
  enrolmentId: string;
  recordAvailable: boolean;
  /** yyyy-MM-dd; null while unsigned. */
  signedOn: string | null;
}

/**
 * The member's training record (Ausbildungsnachweis) under a licence-class enrolment: one quiet
 * download link and whether they signed it at the school (PD-0018). Hidden when there is no record.
 */
const PortalRecordRow = ({ memberId, enrolmentId, recordAvailable, signedOn }: PortalRecordRowProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!recordAvailable) return null;

  const download = async () => {
    setBusy(true);
    setError(null);
    const result = await dispatch(downloadPortalRecord({ memberId, enrolmentId }));
    setBusy(false);
    if (downloadPortalRecord.fulfilled.match(result)) return;
    setError(
      result.payload?.statusCode === 409 ? t("portal.progress.schoolIncomplete") : t("training.record.failed")
    );
  };

  return (
    <div className="mt-2 text-sm" data-testid="portal-record-row">
      <button
        type="button"
        className="inline-flex items-center gap-1 text-primary-40 dark:text-primary-90 hover:underline"
        disabled={busy}
        onClick={download}
      >
        <FileDown size={16} />
        <span>{t("portal.progress.record")}</span>
      </button>
      <p className="text-grey-50 dark:text-grey-70">
        {signedOn
          ? t("portal.progress.signedOn", { date: formatDateOnly(signedOn, getDisplayLocale()) })
          : t("portal.progress.unsigned")}
      </p>
      {error && <p className="text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
};

export default PortalRecordRow;
